import { Clock, Activity, Cpu } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDuration } from "@/lib/format-utils";

/**
 * The three clocks, presented side by side (PI-SPEC-SW-987).
 *
 * The argument this epic exists to end was an argument about ONE number. This
 * panel is the surface that makes the replacement legible: three values,
 * labelled with the question each answers, in the same view.
 *
 *   ELAPSED — how long from started to delivered, idle included.
 *   ACTIVE  — how much of that was working time. A THRESHOLD-BASED ESTIMATE.
 *   COMPUTE — how much machine work it took.
 *
 * They are NEVER summed and active time is NOT a corrected, true or adjusted
 * elapsed time. Presenting it that way would collapse three answers back into
 * one argument about which figure is right.
 */

/** The idle threshold applied by the API, in minutes (the measured p95). */
export const ACTIVE_TIME_IDLE_THRESHOLD_MINUTES = 5.2;

export const THREE_CLOCKS_INTRO =
  "Three separate clocks, never summed and never interchangeable. Each answers a different " +
  "question, so none of them is a corrected version of another.";

export const ELAPSED_CLOCK_QUESTION = "How long did it take from start to delivered?";

export const ELAPSED_CLOCK_EXPLANATION =
  "Wall-clock time from when work started to when it completed. Includes idle time, queueing, " +
  "overnight gaps and weekends, so it is not a measure of effort.";

export const ACTIVE_CLOCK_QUESTION = "How much of that was working time?";

export const ACTIVE_CLOCK_EXPLANATION =
  "Reconstructed from the session event stream by discarding gaps of " +
  `${String(ACTIVE_TIME_IDLE_THRESHOLD_MINUTES)} minutes or more as idle. ` +
  `${String(ACTIVE_TIME_IDLE_THRESHOLD_MINUTES)} minutes is the measured 95th percentile ` +
  "of 16,678 observed intervals between consecutive events, not a round number chosen by judgement. " +
  "Every value is an ESTIMATE, not a bound: the event stream is throttled and coalesced by design, so work that " +
  "emitted no event is invisible to it, and absence of events is not evidence of absence of work.";

export const COMPUTE_CLOCK_QUESTION = "How much machine work did it take?";

export const COMPUTE_CLOCK_EXPLANATION =
  "Tokens, steps and in-turn wall clock, recorded per attempt and reported as two figures - " +
  "first-attempt success and total including retries - because total compute measures struggle " +
  "rather than difficulty.";

/**
 * Why the active and compute clocks carry no aggregate figure on this page.
 *
 * Session events are attributed to an epic, a feature and a task, but NOT to a
 * user, so there is no defensible way to roll active time up into a per-user or
 * per-period total. Compute is recorded per attempt for the same reason. The
 * honest answer is to say so and point at the per-item figure rather than
 * publish an aggregate the data does not support - which is exactly the failure
 * this epic exists to remove.
 */
export const NOT_AGGREGATED_EXPLANATION =
  "Not aggregated here. Session events carry no user attribution, so an active-time total per user " +
  "or per period would not be derivable from the data. The measured value is available per feature " +
  "and per task from the actuals endpoint.";

export const COMPUTE_NOT_AGGREGATED_EXPLANATION =
  "Not aggregated here. Compute is recorded per attempt against a feature or task and is reported " +
  "as two figures that must not be collapsed into one, so it is read per item from the actuals " +
  "endpoint.";

/** Rendered when a clock has no measurement. NEVER a zero and never a bare dash. */
export const NOT_MEASURED_LABEL = "Not measured";

/**
 * Rendered when a clock IS measured per item but is not rolled up on this
 * surface. Distinct from NOT_MEASURED_LABEL on purpose: claiming a clock was
 * not measured when a measured per-item value exists is the same
 * absence-versus-zero confusion the null semantics exist to prevent, one level
 * up. "Not aggregated" says the aggregate is missing; "Not measured" says the
 * measurement is.
 */
export const NOT_AGGREGATED_LABEL = "Not aggregated";

interface ClockCardProps {
  icon: typeof Clock;
  title: string;
  question: string;
  /** Formatted value, or null to render the explicit unmeasured/unaggregated state. */
  value: string | null;
  /**
   * Label to show when `value` is null. Defaults to "Not measured"; pass
   * NOT_AGGREGATED_LABEL when the clock is measured per item but not summed
   * here.
   */
  emptyLabel?: string;
  explanation: string;
  /** Extra note shown under the explanation (e.g. why there is no aggregate). */
  note?: string;
  testId: string;
  accentClass: string;
}

function ClockCard({
  icon: Icon,
  title,
  question,
  value,
  emptyLabel = NOT_MEASURED_LABEL,
  explanation,
  note,
  testId,
  accentClass,
}: ClockCardProps) {
  return (
    <div
      className="flex flex-1 flex-col gap-1.5 rounded-xl border border-border/60 p-3"
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5">
        <Icon className={cn("h-3.5 w-3.5", accentClass)} aria-hidden="true" />
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {title}
        </span>
      </div>

      {/* The value, or an explicit not-measured state. A null measurement is
          never rendered as 0 and never as a dash that reads as zero. */}
      <div className={cn("text-lg font-semibold", value === null && "text-sm text-muted-foreground")}>
        {value ?? emptyLabel}
      </div>

      <p className="text-xs font-medium">{question}</p>
      <p className="text-xs leading-relaxed text-muted-foreground">{explanation}</p>
      {note ? <p className="text-xs leading-relaxed text-muted-foreground">{note}</p> : null}
    </div>
  );
}

interface ThreeClocksPanelProps {
  /**
   * Aggregate ELAPSED minutes for the current view, or null when nothing in the
   * period carries an elapsed measurement.
   */
  elapsedMinutes: number | null;
  className?: string;
}

/**
 * Elapsed, active and compute as three separate values with the question each
 * answers.
 *
 * Only the elapsed figure is aggregated here; active and compute render their
 * explicit not-measured state with the reason, because neither can be attributed
 * to a user. No element in this panel adds two clocks together, and none
 * describes one as a correction to another.
 */
export function ThreeClocksPanel({ elapsedMinutes, className }: ThreeClocksPanelProps) {
  return (
    <section className={cn("rounded-2xl border border-border/60 p-3", className)} data-testid="three-clocks-panel">
      <h2 className="mb-1 text-sm font-medium">Three clocks</h2>
      <p className="mb-3 text-xs leading-relaxed text-muted-foreground" data-testid="three-clocks-intro">
        {THREE_CLOCKS_INTRO}
      </p>

      <div className="flex flex-col gap-2 md:flex-row">
        <ClockCard
          icon={Clock}
          title="Elapsed"
          question={ELAPSED_CLOCK_QUESTION}
          value={elapsedMinutes === null ? null : formatDuration(elapsedMinutes)}
          explanation={ELAPSED_CLOCK_EXPLANATION}
          testId="clock-elapsed"
          accentClass="text-rose-400"
        />
        <ClockCard
          icon={Activity}
          title="Active"
          question={ACTIVE_CLOCK_QUESTION}
          value={null}
          emptyLabel={NOT_AGGREGATED_LABEL}
          explanation={ACTIVE_CLOCK_EXPLANATION}
          note={NOT_AGGREGATED_EXPLANATION}
          testId="clock-active"
          accentClass="text-emerald-400"
        />
        <ClockCard
          icon={Cpu}
          title="Compute"
          question={COMPUTE_CLOCK_QUESTION}
          value={null}
          emptyLabel={NOT_AGGREGATED_LABEL}
          explanation={COMPUTE_CLOCK_EXPLANATION}
          note={COMPUTE_NOT_AGGREGATED_EXPLANATION}
          testId="clock-compute"
          accentClass="text-sky-400"
        />
      </div>
    </section>
  );
}
